import { useState, useRef } from "react";
import axios from "axios";
import { UploadCloud, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import API_BASE_URL from "../../config";

const _base = process.env.REACT_APP_BACKEND_URL || API_BASE_URL;
const API = _base.endsWith('/api') ? _base : `${_base}/api`;

export default function ImageUpload({ onUploadSuccess, currentImage, onRemove, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data" 
      } : {
        "Content-Type": "multipart/form-data"
      };

      const { data } = await axios.post(`${API}/upload`, formData, { 
        withCredentials: true,
        headers
      });
      
      toast.success("Image uploaded successfully");
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL || API_BASE_URL}${data.url}`;
      onUploadSuccess(fullUrl);
    } catch (err) {
      toast.error("Failed to upload image");
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleContainerClick = () => {
    if (!currentImage && !uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
      />
      
      {currentImage ? (
        <div className="relative group rounded-xl overflow-hidden border border-[#E2E8E4]">
          <img src={currentImage} alt="Uploaded preview" className="w-full h-48 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button 
              type="button"
              onClick={onRemove}
              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <div 
          onClick={handleContainerClick}
          className={`border-2 border-dashed border-[#E2E8E4] rounded-xl hover:border-[#3FAF7A] hover:bg-[#F7FAF8] transition-colors flex flex-col items-center justify-center min-h-[120px] cursor-pointer ${uploading ? 'opacity-50 cursor-wait' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-[#5C6B64]">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm font-medium">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-[#5C6B64]">
              <div className="p-3 bg-white rounded-full shadow-sm">
                <ImageIcon className="w-6 h-6 text-[#123D2B]" />
              </div>
              <span className="text-sm font-medium">Click to upload image</span>
              <span className="text-xs text-[#5C6B64]">JPEG, PNG, WebP up to 10MB</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
